import { useState, useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Clock, Plus, Search, Star, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEntityHistory, EntityType } from "@/hooks/useEntityHistory";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface EntityItem {
	id: string;
	name: string;
	metadata?: Record<string, any>;
}

interface EntitySearchPopoverProps {
	entityType: EntityType;
	searchQuery: string;
	onSearchChange: (query: string) => void;
	searchResults: EntityItem[];
	isSearching: boolean;
	selectedIds: string[];
	onAddItem: (item: EntityItem) => void;
	onRemoveItem: (id: string) => void;
	placeholder?: string;
	icon: React.ReactNode;
	iconBgColor?: string;
	iconBgColorDark?: string;
	iconColor?: string;
	iconColorDark?: string;
	resultBgColor?: string;
	resultBgColorDark?: string;
	resultHoverColor?: string;
	resultHoverColorDark?: string;
	noItemsText?: string;
	recentLimit?: number;
	showFavorites?: boolean;
	allAvailableItems?: EntityItem[];
	onCreateNew?: () => void;
}

export const EntitySearchPopover = ({
	entityType,
	searchQuery,
	onSearchChange,
	searchResults,
	isSearching,
	selectedIds,
	onAddItem,
	onRemoveItem,
	placeholder,
	icon,
	iconBgColor = "bg-blue-100",
	iconBgColorDark = "",
	iconColor = "text-blue-500",
	resultBgColor = "bg-blue-50",
	resultBgColorDark = "dark:bg-blue-900/30",
	resultHoverColor = "hover:bg-blue-100",
	resultHoverColorDark = "dark:hover:bg-blue-900/50",
	noItemsText,
	recentLimit = 5,
	showFavorites = true,
	allAvailableItems = [],
	onCreateNew
}: EntitySearchPopoverProps) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { getRecentHistory } = useEntityHistory();
	const [recentItems, setRecentItems] = useState<EntityItem[]>([]);
	const [favoriteItems, setFavoriteItems] = useState<EntityItem[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<'recent' | 'favorites'>('recent');
	const inputRef = useRef<HTMLInputElement>(null);

	// Load recent items and favorites when the popover opens
	useEffect(() => {
		if (isOpen && !searchQuery.trim()) {
			loadRecentItems();
			loadFavoriteItems();
		}
	}, [isOpen, searchQuery]);

	const loadRecentItems = async () => {
		const history = await getRecentHistory();
		const filtered = history
			.filter(entry => entry.entity_type === entityType)
			.slice(0, recentLimit)
			.map(entry => ({
				id: entry.entity_id,
				name: entry.entity_name,
				metadata: entry.metadata
			}));
		setRecentItems(filtered);
	};

	const loadFavoriteItems = async () => {
		if (!showFavorites || !user) return;

		try {
			// Use the same RPC function as RecentHistoryPopover
			const { data: favData, error } = await supabase
				.rpc('get_user_favorites_with_details', {
					p_user_id: user.id,
					p_entity_type: entityType,
					p_limit: 20
				});

			if (error) throw error;

			if (favData) {
				// Format the data to match EntityItem interface
				const formatted = favData.map((fav: any) => ({
					id: fav.entity_id,
					name: fav.entity_name,
					metadata: fav.metadata
				}));
				setFavoriteItems(formatted);
			} else {
				setFavoriteItems([]);
			}
		} catch (error) {
			console.error('Error loading favorite items:', error);
			setFavoriteItems([]);
		}
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onSearchChange(e.target.value);
	};

	const handleAddItem = (item: EntityItem) => {
		if (!selectedIds.includes(item.id)) {
			onAddItem(item);
		}
	};

	const handleInputFocus = () => {
		if (!searchQuery.trim()) {
			loadRecentItems();
			loadFavoriteItems();
		}
	};

	// Get display name for selected items
	const getSelectedItemName = (id: string): string | null => {
		// First check in allAvailableItems if provided
		if (allAvailableItems.length > 0) {
			const item = allAvailableItems.find(i => i.id === id);
			if (item) return item.name;
		}
		// Fallback to recent items
		const recentItem = recentItems.find(i => i.id === id);
		return recentItem?.name || null;
	};

	return (
		<div className="space-y-2 p-2.5 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className={cn("w-5 h-5 rounded-sm flex items-center justify-center", iconBgColor)}>
						<div className={cn("h-3 w-3", iconColor)}>
							{icon}
						</div>
					</div>
					<span className="text-sm font-semibold text-gray-800 dark:text-gray-200 capitalize">
						{t(`calendar.relations.${entityType}`, entityType)}
					</span>
				</div>
				{onCreateNew && entityType !== 'match' && (
					<button
						type="button"
						onClick={onCreateNew}
						title={t('common.addNew', 'Add new')}
						className={cn("flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-sm transition-colors outline-none border-0", iconColor, iconBgColor, iconBgColorDark, resultHoverColor, resultHoverColorDark)}
					>
						<Plus className="h-3 w-3" />
						{t('common.new', 'New')}
					</button>
				)}
			</div>
			<div className="space-y-1.5">
				<Popover open={isOpen} onOpenChange={setIsOpen}>
					<PopoverTrigger asChild>
						<div className="relative">
							<Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
							<Input
								ref={inputRef}
								value={searchQuery}
								onChange={handleSearchChange}
								onFocus={handleInputFocus}
								placeholder={placeholder || t(`calendar.relations.search${entityType}`, `Search ${entityType}...`)}
								className="h-7 text-xs pl-7"
							/>
						</div>
					</PopoverTrigger>
					<PopoverContent 
						className="w-[300px] p-0" 
						align="start"
						onOpenAutoFocus={(e) => e.preventDefault()}
					>
						{/* Show search results when typing */}
						{searchQuery.trim() ? (
							<div className="p-2">
								{searchResults.length > 0 && (
									<div className="space-y-1 max-h-64 overflow-y-auto">
										{searchResults.map((item) => (
											<div
												key={item.id}
												onClick={() => {
													handleAddItem(item);
													setIsOpen(false);
													onSearchChange('');
												}}
												className={cn(
													"flex items-center justify-between p-2 text-xs rounded-sm cursor-pointer transition-colors",
													resultBgColor,
													resultBgColorDark,
													resultHoverColor,
													resultHoverColorDark,
													selectedIds.includes(item.id) && "opacity-50 pointer-events-none"
												)}
											>
												<span className="truncate flex-1">{item.name}</span>
												{selectedIds.includes(item.id) ? (
													<span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Added</span>
												) : (
													<Plus className={cn("h-3 w-3 flex-shrink-0 ml-2", iconColor)} />
												)}
											</div>
										))}
									</div>
								)}

								{isSearching && (
									<div className="p-2 text-xs text-gray-500 dark:text-gray-400 text-center">
										{t('common.searching', 'Searching...')}
									</div>
								)}

								{!isSearching && searchResults.length === 0 && (
									<div className="p-2 text-xs text-gray-500 dark:text-gray-400 text-center">
										{t('common.noResults', 'No results found')}
									</div>
								)}
							</div>
						) : (
							<>
								{/* Tabs for Recent and Favorites */}
								<div className="flex border-b">
									<button
										onClick={() => setActiveTab('recent')}
										className={cn(
											"flex-1 px-3 py-2 text-xs font-medium transition-colors",
											activeTab === 'recent'
												? "border-b-2 border-blue-500 text-blue-500 bg-blue-50"
												: "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
										)}
									>
										<div className="flex items-center justify-center gap-1.5">
											<Clock size={14} />
											{t('search.recent', 'Recent')}
										</div>
									</button>
									{showFavorites && (
										<button
											onClick={() => setActiveTab('favorites')}
											className={cn(
												"flex-1 px-3 py-2 text-xs font-medium transition-colors",
												activeTab === 'favorites'
													? "border-b-2 border-yellow-500 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30"
													: "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
											)}
										>
											<div className="flex items-center justify-center gap-1.5">
												<Star size={14} />
												{t('search.favorites', 'Favorites')}
											</div>
										</button>
									)}
								</div>

								{/* Tab Content */}
								<div className="p-2">
									{activeTab === 'recent' && (
										<>
											{recentItems.length > 0 ? (
												<div className="space-y-1 max-h-64 overflow-y-auto">
													{recentItems.map((item) => (
														<div
															key={item.id}
															onClick={() => {
																handleAddItem(item);
																setIsOpen(false);
															}}
															className={cn(
																"flex items-center justify-between p-2 text-xs rounded-sm cursor-pointer transition-colors",
																resultBgColor,
																resultHoverColor,
																selectedIds.includes(item.id) && "opacity-50 pointer-events-none"
															)}
														>
															<span className="truncate flex-1">{item.name}</span>
															{selectedIds.includes(item.id) ? (
																<span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Added</span>
															) : (
																<Plus className={cn("h-3 w-3 flex-shrink-0 ml-2", iconColor)} />
															)}
														</div>
													))}
												</div>
											) : (
												<div className="p-4 text-xs text-gray-500 dark:text-gray-400 text-center">
													{t('search.noRecentItems', 'No recent items')}
												</div>
											)}
										</>
									)}

									{activeTab === 'favorites' && (
										<>
											{favoriteItems.length > 0 ? (
												<div className="space-y-1 max-h-64 overflow-y-auto">
													{favoriteItems.map((item) => (
														<div
															key={item.id}
															onClick={() => {
																handleAddItem(item);
																setIsOpen(false);
															}}
															className={cn(
																"flex items-center justify-between p-2 text-xs rounded-sm cursor-pointer transition-colors",
																resultBgColor,
																resultHoverColor,
																selectedIds.includes(item.id) && "opacity-50 pointer-events-none"
															)}
														>
															<span className="truncate flex-1">{item.name}</span>
															{selectedIds.includes(item.id) ? (
																<span className="text-xs text-gray-500 dark:text-gray-400 ml-2">Added</span>
															) : (
																<Plus className={cn("h-3 w-3 flex-shrink-0 ml-2", iconColor)} />
															)}
														</div>
													))}
												</div>
											) : (
												<div className="p-4 text-xs text-gray-500 dark:text-gray-400 text-center">
													{t('search.noFavorites', 'No favorites yet')}
													<div className="text-[10px] text-gray-400 mt-1">
														{t('search.noFavoritesHint', 'Use the ⭐ button on items to add them')}
													</div>
												</div>
											)}
										</>
									)}
								</div>
							</>
						)}
					</PopoverContent>
				</Popover>

				{/* Selected Items */}
				{selectedIds.length > 0 ? (
					<div className="space-y-1">
						{selectedIds.map((id) => {
							const name = getSelectedItemName(id);
							return name ? (
								<div key={id} className="flex items-center justify-between p-2 text-xs bg-gray-50 dark:bg-gray-800/50 rounded-sm">
									<span className="truncate">{name}</span>
									<button
										type="button"
										onClick={() => onRemoveItem(id)}
										className="text-red-500 hover:text-red-700 transition-colors"
									>
										<Trash2 className="h-3 w-3" />
									</button>
								</div>
							) : null;
						})}
					</div>
				) : (
					<div className="text-xs text-gray-500 dark:text-gray-400 text-center py-1.5 bg-gray-50 rounded-sm">
						{noItemsText || t(`calendar.relations.no${entityType}Linked`, `No ${entityType} linked`)}
					</div>
				)}
			</div>
		</div>
	);
};
